/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { mutatorAction } from "satcheljs";

import { CreateActionsBaseParams } from "../CreateActionsBaseParams";

import { EntityMapActions } from "./EntityMapActions";
import { EntityMapState } from "./EntityMapState";
import { GetId } from "./GetId";

/**
 * Creates actions for setting, removing or updating each entity by their unique identifier in a map when using generic store.
 * @template TKey The type of the unique identifier for each entity.
 * @template TEntity The type of the entity value.
 * @template TState The type of the state.
 * @param params The parameters.
 * @returns The created actions.
 */
export function createEntityMapActions<TKey, TEntity, TState extends EntityMapState<TKey, TEntity>>(
    params: CreateActionsBaseParams<TState>
): EntityMapActions<TKey, TEntity> {
    const { namespace, store } = params;

    return {
        deleteMap: mutatorAction(`${namespace}DeleteMap`, (values: TEntity[], getId: GetId<TKey, TEntity>) => {
            for (const value of values) {
                store().valuesByIds.delete(getId(value));
            }
        }),

        setMap: mutatorAction(`${namespace}SetMap`, (values: TEntity[], getId: GetId<TKey, TEntity>) => {
            store().valuesByIds.clear();

            for (const value of values) {
                store().valuesByIds.set(getId(value), value);
            }
        }),

        updateMap: mutatorAction(`${namespace}UpdateMap`, (values: TEntity[], getId: GetId<TKey, TEntity>) => {
            for (const value of values) {
                store().valuesByIds.set(getId(value), value);
            }
        })
    };
}
